import { useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ESGFitQuiz from "@/components/ESGFitQuiz";
import { Link } from "react-router-dom";
import { applySeo } from "@/lib/seo";

export default function CalculatorESGFinder() {
  useEffect(() => {
    applySeo({
      title: "Nachhaltiger ETF-Finder – Grüne Geldanlage",
      description: "Welcher nachhaltige ETF passt zu Ihnen? Unser ESG-Fit-Quiz filtert aus 25 kuratierten SRI- und ESG-ETFs anhand Ihrer Ausschlusskriterien, Risikoneigung und Prioritäten die passende Empfehlung.",
      canonicalPath: "/rechner/nachhaltiger-etf-finder",
    });
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="calc-esg-finder-page">
      <Header />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-8">
        <div className="overline mb-4">Rechner</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6">
          Nachhaltiger ETF-Finder
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl mb-10">
          Welcher nachhaltige ETF passt zu Ihnen? Fünf kurze Fragen zu Ausschlusskriterien,
          Risikoneigung und Prioritäten – am Ende erhalten Sie eine persönliche Empfehlung
          aus unserer kuratierten Auswahl von 25 SRI- und ESG-Aktien-ETFs, inklusive Begründung.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20" data-testid="calc-esg-finder-tool">
        <div className="border border-neutral-200 bg-white p-6 md:p-10">
          <div className="overline mb-3">Rechner · ESG-Fit-Quiz</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-2">Fünf Fragen zu Ihrer ETF-Passung</h2>
          <p className="text-neutral-600 mb-8 max-w-3xl">
            Beantworten Sie fünf kurze Fragen zu Ausschlüssen, Risiko, Kosten, Region und Ausschüttung.
            Am Ende erhalten Sie drei passende ETFs aus unserer kuratierten Auswahl – mit Begründung.
          </p>
          <ESGFitQuiz />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="prose-editorial max-w-none">
          <h2>Warum ein Quiz statt eines reinen ETF-Screeners?</h2>
          <p>
            Wer nach einem nachhaltigen ETF sucht, findet im Netz vor allem Ranglisten:
            die günstigsten SRI-ETFs, die größten ESG-Fonds, die mit der besten Performance.
            Was dabei meist fehlt, ist der Bezug zur eigenen Situation. Ein günstiger ETF
            nützt wenig, wenn er genau die Branche nicht ausschließt, die einem persönlich
            wichtig ist – und ein ETF mit dem strengsten Ausschlusskatalog kann trotzdem
            die falsche Wahl sein, wenn Risikoprofil oder Ausschüttungsart nicht passen.
          </p>
          <p>
            Unser ESG-Fit-Quiz dreht die Reihenfolge um: Statt mit einer Liste von ETFs
            zu starten, beginnt es mit Ihnen. Fünf kurze Fragen zu Ausschlusskriterien,
            Risikoneigung, Kostenfokus, regionalem Schwerpunkt und Ausschüttungspräferenz
            genügen, um aus unserer redaktionell kuratierten Auswahl von 25 SRI- und
            ESG-Aktien-ETFs eine persönliche Empfehlung mit Begründung zu erhalten.
          </p>

          <h2>Wie funktioniert das Matching?</h2>
          <p>
            Jeder ETF in unserer Datenbank ist anhand seiner dokumentierten Indexmethodik
            eingeordnet: welche Branchen er ausschließt, ob er einem strengen SRI-Ansatz
            mit vollständigem Ausschlusskatalog folgt oder einem Best-in-Class-Ansatz mit
            CO₂-Reduktionsziel, welche TER er hat, welchen regionalen Fokus und welche
            Ausschüttungspolitik. Ihre Antworten werden gegen diese Merkmale gewichtet:
            Jede erfüllte Ausschlussvorgabe, jede Übereinstimmung bei Risikoprofil, Region
            und Ausschüttungsart erhöht die Punktzahl des jeweiligen ETFs. Am Ende zeigen
            wir Ihnen die drei am besten passenden ETFs – mit einer kurzen Begründung,
            warum genau dieser ETF zu Ihren Angaben passt.
          </p>

          <h2>Was die Auswahl bewusst nicht leistet</h2>
          <p>
            Unsere Datenbank ist eine kuratierte Auswahl, kein Live-Abbild des gesamten
            ETF-Marktes. Wir aktualisieren TER, Fondsgröße und Indexmethodik regelmäßig
            von Hand, statt sie automatisiert von einer Live-Schnittstelle zu beziehen –
            das macht die Auswahl robuster gegen kurzfristige Datenfehler, bedeutet aber
            auch, dass neu aufgelegte ETFs nicht sofort erscheinen. Die Zuordnung der
            Ausschlusskriterien orientiert sich an der öffentlich dokumentierten Methodik
            der jeweiligen Indexfamilie und wurde nicht für jeden einzelnen Fonds individuell
            vom Emittenten bestätigt. Das Quiz ersetzt keine individuelle Anlageberatung
            und keine Prüfung des aktuellen Verkaufsprospekts vor einer Investitionsentscheidung.
          </p>

          <h2>Praxis-Tipp: Kombinieren Sie das Quiz mit unseren anderen Rechnern</h2>
          <p>
            Das ESG-Fit-Quiz hilft Ihnen, einen passenden ETF zu finden. Wie sich Ihr
            Investment über die Jahre entwickeln könnte, zeigt der{" "}
            <Link to="/rechner/nachhaltigkeits-rendite">Nachhaltigkeits-Rendite-Rechner</Link>.
            Was ein nachhaltiges Portfolio an Rendite gegenüber einem klassischen ETF
            kostet oder einbringt, beantwortet der{" "}
            <Link to="/rechner/green-premium">Green Premium Rechner</Link>. Und wer die
            ökologische Wirkung seines Investments einschätzen möchte, findet im{" "}
            <Link to="/rechner/co2-fussabdruck">CO₂-Fußabdruck-Portfolio-Rechner</Link>{" "}
            die passende Ergänzung. Erst im Zusammenspiel der Rechner entsteht ein
            vollständiges Bild aus Passung, Rendite und Wirkung.
          </p>

          <h2>Die fünf Fragen im Überblick</h2>
          <p>
            Jede Frage im Quiz ist bewusst so formuliert, dass sie eine der zentralen
            Anlage-Entscheidungen abbildet, die Sie ohnehin treffen müssen:
          </p>
          <ul>
            <li><strong>Ausschlusskriterien:</strong> Welche Branchen sollen definitiv nicht in Ihrem Portfolio landen? Rüstung, fossile Energien, Atomkraft, Tabak, Glücksspiel oder Konflikte mit Menschenrechten – jede Auswahl grenzt die Kandidatenliste ein.</li>
            <li><strong>Risikoprofil:</strong> Wie viel Schwankungsbreite können und wollen Sie aushalten? Ein breit gestreuter Welt-ETF verhält sich anders als ein Themen-ETF für saubere Energien.</li>
            <li><strong>Kosten- vs. Ansatz-Fokus:</strong> Priorisieren Sie niedrige TER oder einen möglichst strengen Nachhaltigkeitsansatz? Beides gleichzeitig gibt es selten in Reinform.</li>
            <li><strong>Regionaler Schwerpunkt:</strong> Global, Industrieländer, Europa oder ein spezielles Thema? Die Region beeinflusst Rendite, Klumpenrisiko und Währungseffekte.</li>
            <li><strong>Ausschüttungspolitik:</strong> Thesaurierend für den langfristigen Vermögensaufbau oder ausschüttend für passives Einkommen? Beide Wege haben ihre Berechtigung.</li>
          </ul>

          <h2>Von der Empfehlung zur konkreten Entscheidung</h2>
          <p>
            Die drei ausgeworfenen ETFs sind ein Startpunkt, kein Endpunkt. Bevor Sie
            eine Order platzieren, empfehlen wir drei Prüfungen: Erstens, öffnen Sie das
            aktuelle Fondsfactsheet und den Verkaufsprospekt – TER und Fondsgröße können
            sich ändern. Zweitens, vergleichen Sie das SFDR-Label (Artikel 8 oder 9) mit
            Ihrem Anspruch; die Details dazu erklären wir in unserem Artikel zur{" "}
            <Link to="/artikel/sfdr-2-0-reform">SFDR 2.0-Reform</Link>. Drittens, prüfen
            Sie, ob Ihr Depotanbieter den gewählten ETF als Sparplan zu günstigen
            Konditionen anbietet – ein 1,50-Euro-Ordergebühr-Aufschlag pro Monat frisst
            über zehn Jahre spürbar Rendite.
          </p>

          <h2>Was nachhaltige Anlage in der Praxis bedeutet</h2>
          <p>
            Ein nachhaltiger ETF ist kein Garant für ökologisch reine Portfolios – aber
            auch keine bloße Marketingfassade. Zwischen diesen beiden Extremen liegt ein
            ganzes Spektrum: SRI-Indizes mit strengem Ausschlusskatalog, ESG-Screened-ETFs
            mit Best-in-Class-Ansatz, Paris-Aligned-Benchmarks mit CO₂-Reduktionspfad und
            Themen-ETFs für spezifische Sektoren wie saubere Energien oder Kreislaufwirtschaft.
            Das Quiz sortiert Sie in dieses Spektrum ein und liefert ETFs, die zu Ihrer
            Position darin passen. Wer die Bewertungsgrundlagen dahinter verstehen möchte,
            findet in unserem Grundlagenartikel{" "}
            <Link to="/artikel/esg-kriterien-einfach-erklaert">ESG-Kriterien einfach erklärt</Link>{" "}
            die konzeptionelle Basis. Und wer im nächsten Schritt Greenwashing-Signale
            erkennen möchte, sollte unseren Praxisartikel{" "}
            <Link to="/artikel/greenwashing-erkennen">Greenwashing erkennen</Link>{" "}gelesen haben.
          </p>

          <h2>Fazit</h2>
          <p>
            Nachhaltiges Investieren beginnt mit der ehrlichen Frage, was Ihnen selbst
            wichtig ist – nicht mit der Frage, welcher ETF gerade auf einer Bestenliste
            steht. Das ESG-Fit-Quiz zwingt zu dieser ehrlichen Selbsteinschätzung und
            übersetzt sie in konkrete Produktvorschläge. Kombiniert mit unseren
            Rechnern und Grundlagenartikeln entsteht daraus in wenigen Minuten ein
            fundierter Ausgangspunkt für Ihre nächste Depot-Entscheidung.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
